import { createBotInput } from "../game/bot";
import {
  EMPTY_INPUT,
  TICK_RATE,
  createMatchState,
  resetMatchForDemo,
  stepMatch,
  type ActionInput,
  type GameMode,
  type MatchEvent,
  type MatchState,
  type PlayerSlot
} from "../game/simulation";

export interface LocalMatchDriverOptions {
  mode: GameMode;
  readHumanInput: () => ActionInput;
  onSnapshot: (state: MatchState) => void;
  onEvents?: (events: MatchEvent[]) => void;
  automateHuman?: boolean;
}

const DUEL_SLOTS: PlayerSlot[] = [
  { id: "p1", name: "YOU", team: "blue", kind: "human" },
  { id: "p2", name: "MICA", team: "orange", kind: "bot" }
];

const TEAM_SLOTS: PlayerSlot[] = [
  { id: "p1", name: "YOU", team: "blue", kind: "human" },
  { id: "p2", name: "MICA", team: "orange", kind: "bot" },
  { id: "p3", name: "PIP", team: "blue", kind: "bot" },
  { id: "p4", name: "QUILL", team: "orange", kind: "bot" }
];

export class LocalMatchDriver {
  private state: MatchState;
  private timer: number | null = null;
  private readonly options: LocalMatchDriverOptions;

  constructor(options: LocalMatchDriverOptions) {
    this.options = options;
    this.state = this.createState();
  }

  start(): void {
    if (this.timer !== null) return;
    this.options.onSnapshot(this.state);
    this.timer = window.setInterval(() => this.tick(), 1000 / TICK_RATE);
  }

  stop(): void {
    if (this.timer !== null) window.clearInterval(this.timer);
    this.timer = null;
  }

  reset(): void {
    resetMatchForDemo(this.state);
    this.options.onSnapshot(this.state);
  }

  snapshot(): MatchState {
    return this.state;
  }

  private createState(): MatchState {
    return createMatchState({
      id: `local-${this.options.mode}`,
      mode: this.options.mode,
      slots: this.options.mode === "duel" ? DUEL_SLOTS : TEAM_SLOTS,
      seed: this.options.mode === "duel" ? 173 : 227
    });
  }

  private tick(): void {
    const inputs: Record<string, ActionInput> = {};
    for (const player of this.state.players) {
      if (player.kind === "human" && !this.options.automateHuman) {
        inputs[player.id] = this.options.readHumanInput();
      } else {
        inputs[player.id] = createBotInput(this.state, player.id);
      }
    }
    if (Object.keys(inputs).length === 0) inputs.p1 = { ...EMPTY_INPUT };
    const events = stepMatch(this.state, inputs);
    if (events.length > 0) this.options.onEvents?.(events);
    this.options.onSnapshot(this.state);
  }
}
